// Schreiben-Assistent Schritt 1: Vorlage wählen – Suche, Zielgruppe, Favoriten, „Freier Text“.
// Nur schlichte Vorlagen (ohne Tabellen); die Lücken „[bitte … eintragen]“ werden zu Eingabefeldern.
import React, { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlass, NotePencil, Star } from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { istSchlichteVorlage, kategorieLabel } from '../../../lib/dokumente/briefVorlagen';
import { vorlageZuBrief } from '../../../lib/dokumente/briefDaten';
import { ladeFavoriten, setzeFavorit, VORLAGEN_SPALTEN } from '../../../lib/schreiben/favoriten';
import { normalisiere, zaehleLuecken } from '../../../lib/schreiben/platzhalter';
import { ZIELGRUPPEN } from '../../../lib/schreiben/typen';
import { Umschalter } from '../emails/bereich/Formular';
import { START, type SetZustand, type Zustand } from './zustand';

interface Vorlage { id: string; name: string; kategorie: string; beschreibung: string | null; html_template: string; zielgruppe?: string | null; immer_post?: boolean; begleittext?: string | null; briefkopf_id?: string | null; schriftform?: boolean; einschreiben?: string | null; wiedervorlage_tage?: number | null; vorgangsart_id?: string | null }

export function SchrittVorlage({ zustand, setZustand, weiter }: { zustand: Zustand; setZustand: SetZustand; weiter: () => void }) {
  const { mobil } = useMobilModus();
  const [vorlagen, setVorlagen] = useState<Vorlage[] | null>(null);
  const [favoriten, setFavoriten] = useState<Set<string>>(new Set());
  const [suche, setSuche] = useState('');
  const [gruppe, setGruppe] = useState<string>('alle');

  useEffect(() => {
    // Zusatzspalten erst nach der Migration – ohne sie die Grundspalten laden
    // (Etappe 2: briefkopf_id, Etappe 3: schriftform, einschreiben) – fehlt eine Spalte, die nächstkleinere Auswahl
    void (async () => {
      let liste: unknown[] = [];
      for (const spalten of [...VORLAGEN_SPALTEN, '']) {
        const { data, error } = await supabase.from('dokument_vorlagen').select(`id, name, kategorie, beschreibung, html_template${spalten ? `, ${spalten}` : ''}`).eq('aktiv', true).order('name');
        if (!error) { liste = data ?? []; break; }
      }
      setVorlagen((liste as Vorlage[]).filter((v) => istSchlichteVorlage(v.html_template)));
    })();
    void ladeFavoriten().then(setFavoriten);
  }, []);

  const liste = useMemo(() => {
    const s = suche.trim().toLowerCase();
    return (vorlagen ?? [])
      .filter((v) => gruppe === 'alle' || (gruppe === 'fav' ? favoriten.has(v.id) : v.zielgruppe === gruppe))
      .filter((v) => !s || `${v.name} ${v.beschreibung ?? ''} ${kategorieLabel(v.kategorie)}`.toLowerCase().includes(s))
      .sort((a, b) => Number(favoriten.has(b.id)) - Number(favoriten.has(a.id)) || a.name.localeCompare(b.name, 'de'));
  }, [vorlagen, suche, gruppe, favoriten]);

  const waehle = (v: Vorlage | null) => {
    // wege: {} → Schritt Versand setzt die Standardwege neu (z. B. „immer zusätzlich per Post“ der neuen Vorlage)
    if (!v) { setZustand((z) => ({ ...z, vorlageId: null, immerPost: false, begleittext: '', betreff: '', text: START.text, eingaben: {}, wege: {}, briefkopfId: null, schriftform: false, wiedervorlageTage: null, vorgangsartId: null, wiedervorlage: null, optionen: { ...z.optionen, einschreiben: '' } })); weiter(); return; }
    const r = vorlageZuBrief(v.html_template, {});
    const betreff = normalisiere(r.betreff);
    const text = normalisiere(r.text, zaehleLuecken(r.betreff) + 1);
    setZustand((z) => ({ ...z, vorlageId: v.id, immerPost: v.immer_post === true, begleittext: v.begleittext ?? '', betreff, text, eingaben: {}, wege: {}, briefkopfId: v.briefkopf_id ?? null,
      schriftform: v.schriftform === true, wiedervorlageTage: v.wiedervorlage_tage ?? null, vorgangsartId: v.vorgangsart_id ?? null, wiedervorlage: null, optionen: { ...z.optionen, einschreiben: (v.einschreiben ?? '') as Zustand['optionen']['einschreiben'] } }));
    weiter();
  };

  const stern = (id: string) => {
    const an = !favoriten.has(id);
    setFavoriten((f) => { const n = new Set(f); if (an) n.add(id); else n.delete(id); return n; });
    void setzeFavorit(id, an).catch(() => setFavoriten((f) => { const n = new Set(f); if (an) n.delete(id); else n.add(id); return n; }));
  };

  const karte: React.CSSProperties = { borderRadius: 18, padding: 14, gap: 4, border: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--ink)', background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)', fontFamily: 'inherit' };
  return (
    <section className="flex flex-col" style={{ gap: 12 }}>
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <div className="flex min-w-0 flex-1 items-center" style={{ gap: 8, height: 44, padding: '0 12px', borderRadius: 14, background: 'var(--fill2)', minWidth: 200 }}>
          <MagnifyingGlass size={15} color="var(--ink3)" />
          <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Vorlage suchen …" aria-label="Vorlage suchen" autoFocus={!mobil}
            className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit' }} />
        </div>
        <div className="immo-ohne-scrollbar overflow-x-auto">
          <Umschalter hoehe={mobil ? 44 : 34} wert={gruppe} onChange={setGruppe}
            optionen={[{ key: 'alle', label: 'Alle' }, { key: 'fav', label: '★ Favoriten' }, ...ZIELGRUPPEN.map(([k, l]) => ({ key: k, label: l }))]} />
        </div>
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 10 }}>
        <button type="button" onClick={() => waehle(null)} className="immo-hover-flaeche flex flex-col" style={{ ...karte, background: 'var(--acc-soft)' }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 14, fontWeight: 800 }}><NotePencil size={16} weight="bold" />Freier Text</span>
          <span style={{ fontSize: 12.5, color: 'var(--ink2)' }}>Ohne Vorlage, selbst formulieren</span>
        </button>
        {vorlagen === null && <div className="animate-pulse" style={{ height: 90, borderRadius: 18, background: 'var(--fill2)' }} />}
        {liste.map((v) => (
          <div key={v.id} role="button" tabIndex={0} onClick={() => waehle(v)} onKeyDown={(e) => { if (e.key === 'Enter') waehle(v); }}
            className="immo-hover-flaeche relative flex flex-col" style={karte}>
            <span style={{ fontSize: 14, fontWeight: 800, paddingRight: 34 }}>{v.name}</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
              {[kategorieLabel(v.kategorie), ZIELGRUPPEN.find(([k]) => k === v.zielgruppe)?.[1], v.schriftform ? 'Schriftform' : null, v.einschreiben || null, v.immer_post ? 'immer auch per Post' : null].filter(Boolean).join(' · ')}
            </span>
            {v.beschreibung && <span style={{ fontSize: 12.5, color: 'var(--ink2)' }}>{v.beschreibung}</span>}
            <button type="button" aria-label={favoriten.has(v.id) ? 'Aus Favoriten entfernen' : 'Zu Favoriten'} onClick={(e) => { e.stopPropagation(); stern(v.id); }}
              className="absolute flex items-center justify-center" style={{ top: 8, right: 8, width: 32, height: 32, borderRadius: 10, border: 'none', background: 'transparent', cursor: 'pointer' }}>
              <Star size={18} weight={favoriten.has(v.id) ? 'fill' : 'regular'} color={favoriten.has(v.id) ? 'var(--warn)' : 'var(--ink3)'} />
            </button>
          </div>
        ))}
        {vorlagen && liste.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)', padding: 8 }}>Keine Vorlage passt zur Suche.</span>}
      </div>
      {zustand.vorlageId && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Eine Vorlage ist gewählt – eine neue Wahl ersetzt Betreff und Text.</span>}
    </section>
  );
}
